// ============================================================================
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  DOCUMENTAÇÃO PESQUISADA E VALIDADA VIA MCP (31/07/2026) — NÃO APAGAR    ║
// ║  (Regra Magna 213 / Artigo 12 — Vedação ao Retrocesso)                    ║
// ╚══════════════════════════════════════════════════════════════════════════╝
// ----------------------------------------------------------------------------
// 🔴 PROBLEMA: a barra de ferramentas do visualizador (tarja cinza/ferramenta)
//    não mostra o nome do arquivo da URL, mas sim "Procuracao - MARCOS DAL
//    BELLO" (título interno do PDF gravado pelo Word).
//
// ✅ CAUSA RAIZ (VALIDADA via MCP parallel-search — fontes verificáveis):
//    Existem DOIS motores de PDF no navegador, e cada um deriva o título de
//    forma DIFERENTE:
//
//    1. PDFium (viewer nativo do Chrome):
//       Lê o TÍTULO DOS METADADOS INTERNOS do PDF (dicionário /Info e stream
//       XMP dc:title), IGNORANDO o nome da URL e o Content-Disposition.
//       Fonte: https://issues.chromium.org/issues/495162229
//       "Chrome ignores Content-Disposition filename and uses PDF Title
//        metadata... it reads the Title metadata embedded inside the PDF."
//       E: https://www.loslab.com/pdfium-component-doc/.../TPdf.Title.html
//       "setting a title is done through FPDF_SetMetaText or XMP (dc:title)".
//       E: chromium/src/pdf/pdfium/pdfium_document_metadata.cc
//
//    2. PDF.js viewer (Mozilla, customizado — pdf_viewer_wrapper.js):
//       Mostra o NOME DO ARQUIVO DERIVADO DA URL (percent-decoded), não lê
//       metadados nem Content-Disposition. Log: shared.rollup.js →
//       pdf_viewer_wrapper.js:871 → initViewer @ main.js:24
//
// ⚠️ OBSERVAÇÃO CRÍTICA: se o log mostra "pdf_viewer_wrapper.js", o PDF.js
//    pode FALHAR ao interceptar o PDF (ou o viewer não assumir), e o Chrome
//    usa o PDFium nativo. Nesses casos a tarja mostra o TÍTULO INTERNO do PDF.
//    É por isso que a URL pode estar correta (ANEXO_03_...) mas a barra
//    mostrar "Procuracao - [CLIENTE_D]".
//
// ✅ SOLUÇÃO (metadados, documentada no functions/index.js — bloco travado):
//    O backend (getSecureDocumentUrl) DEVE limpar/reescrever os metadados do
//    PDF quando o PDFium renderizar:
//      - pdfDoc.catalog.delete(PDFName.of("Metadata"))   // remove XMP dc:title
//      - pdfDoc.setTitle(nome, { showInWindowTitleBar: true }) // /Info + DisplayDocTitle
//    Isso só roda SE o frontend enviar `title` no payload. Quando não há
//    ?title=, o backend usa o nome real do arquivo no Drive (finalFileName) —
//    que neste caso é "Procuracao - [CLIENTE_D]", por isso aparece.
//
// ❌ O QUE NÃO FUNCIONA (perda de tempo validada):
//    - document.title / setInterval (afeta só a aba, não a tarja do viewer)
//    - Content-Disposition filename= (PDFium ignora — chromium issue acima)
//    - #toolbar=0 (só funciona no PDFium; não esconde a tarja do PDF.js)
//    - Metadados pdf-lib SÓ funcionam se o PDF cair no PDFium; o PDF.js não lê.
//
// 🔧 DECISÃO NA QUAL ESTAMOS (estável):
//    - Tarja azul (header React) REMOVIDA — nome aparece só na tarja do viewer.
//    - Envia `title` ao backend APENAS quando há ?title= na URL (estável),
//      evitando o genérico "Documento de Prova" e o loop/429 no Storage.
//    - useEffect depende de [id] — abre o documento sempre, sem loop.
//
// 📚 ARQUIVOS DE REFERÊNCIA (MANTER):
//    - PESQUISAS_PROFUNDAS/RELATORIO_CONSOLIDADO_SOLUCAO_TITULO_PDF_20260731.md
//    - PESQUISAS_PROFUNDAS/TCREI_PROMPT_PESQUISA_TITULO_PDF_CHROME.md
//    - PESQUISAS_PROFUNDAS/RESULTADO_PESQUISA_3_TITULO_PDF_CHROME_20260731.md
//    - functions/index.js (bloco travado antes de getSecureDocumentUrl)
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../shared/api/firebase/config";
import { sanitizeEvidenceName } from "../../utils/formatters";
import GedSemanticChat from "../../components/ged/GedSemanticChat";

export default function DocumentViewer({ id: idProp }) {
  const id = useMemo(() => {
    return (
      String(idProp || "").trim() ||
      String(window?.location?.pathname || "").split("/view/")[1] ||
      ""
    );
  }, [idProp]);

  const [documentUrl, setDocumentUrl] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);

  const sanitizedName = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let titleParam = urlParams.get("title");
    if (titleParam && titleParam.trim()) {
      try {
        titleParam = decodeURIComponent(titleParam.trim());
      } catch (e) {
        // ignora erro de decode
      }
      return titleParam;
    }
    return sanitizeEvidenceName(metadata?.name);
  }, [metadata?.name, window.location.search]);

  useEffect(() => {
    const safeTitle = String(sanitizedName ?? "").trim();
    document.title = safeTitle ? safeTitle : "ANEXO - PROVA DOCUMENTAL";
  }, [sanitizedName]);

  useEffect(() => {
    let isMounted = true;

    const fetchSecureLink = async () => {
      if (!id) return;
      setIsLoading(true);
      setError(null);
      setDocumentUrl(null);
      setMetadata(null);

      // Envia o título APENAS quando vier do ?title= da URL (estável desde o
      // início, sem depender de metadata). Sem ?title=, o backend usa o nome
      // real do arquivo no Drive com fallback — evitando o genérico "Documento
      // de Prova" e limpando o XMP do Word quando há nome a enviar.
      const urlParams = new URLSearchParams(window.location.search);
      const titleParam = urlParams.get("title");
      const title =
        titleParam && titleParam.trim()
          ? (() => {
              try {
                return decodeURIComponent(titleParam.trim());
              } catch {
                return titleParam.trim();
              }
            })()
          : undefined;

      try {
        const fn = httpsCallable(functions, "getSecureDocumentUrl");
        const payload = await fn({
          driveFileId: id,
          ...(title ? { title } : {}),
        });
        const url = payload?.data?.url;
        const meta = payload?.data?.metadata;

        if (!url) {
          throw new Error("Falha ao obter credencial segura do documento.");
        }

        if (isMounted) setDocumentUrl(String(url));
        if (isMounted)
          setMetadata(
            meta ?? { name: "Documento Sigiloso", mimeType: "application/pdf" },
          );
      } catch (err) {
        if (isMounted) {
          const msg = String(err?.message || err);
          setError(msg || "Erro de comunicação com os cofres do portal.");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchSecureLink();
    return () => {
      isMounted = false;
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900 mb-4"></div>
        <p className="text-slate-700 font-medium">
          Autenticando e extraindo documento dos cofres seguros...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="max-w-lg p-6 bg-white border border-red-200 rounded shadow text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Acesso Negado ou Indisponível
          </h2>
          <p className="text-slate-600 whitespace-pre-wrap">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#1a1c2e]">
      <main className="relative flex flex-1 overflow-hidden bg-[#1a1c2e] h-screen">
        {/* Visualizador de documento — full width sempre em mobile; encolhe em desktop quando chat aberto */}
        <div
          className={`overflow-hidden transition-all duration-300 w-full ${showChat ? "md:w-2/3" : ""}`}
        >
          {documentUrl &&
            (metadata?.mimeType?.startsWith("video/") ? (
              <video
                src={documentUrl}
                controls
                className="w-full h-full object-contain bg-black"
                controlsList="nodownload nofullscreen"
                disablePictureInPicture
              />
            ) : (
              <iframe
                src={documentUrl}
                title={String(sanitizedName)}
                className="w-full h-full border-none"
              />
            ))}
        </div>

        {/* Chat IA — Drawer absoluto em mobile, sidebar em desktop */}
        {showChat && (
          <div className="absolute inset-0 z-50 md:relative md:inset-auto md:z-auto md:w-1/3 md:shrink-0 overflow-hidden">
            <GedSemanticChat
              driveFileId={id}
              onClose={() => setShowChat(false)}
            />
          </div>
        )}
      </main>
    </div>
  );
}
